<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="show"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div class="absolute inset-0 bg-black/50" @click="close"></div>
        <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4">
          <!-- 头部 -->
          <div class="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 class="text-lg font-semibold text-slate-900">待办详情</h3>
            <button @click="close" class="p-1.5 text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <!-- 内容 -->
          <div class="p-6">
            <div v-if="loading" class="flex items-center justify-center py-8">
              <svg class="w-6 h-6 animate-spin text-primary-500" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <template v-else-if="displayItem">
              <!-- 标签行 -->
              <div class="flex items-center gap-2 mb-4">
                <span :class="getPriorityBadgeClass(displayItem.priority)">{{ displayItem.priorityDesc || getPriorityLabel(displayItem.priority) }}</span>
                <span :class="getStatusBadgeClass(displayItem.status)">{{ displayItem.statusDesc || getStatusLabel(displayItem.status) }}</span>
                <span v-if="displayItem.overdue" class="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">逾期</span>
              </div>
              <!-- 标题 -->
              <h4 class="text-xl font-semibold text-slate-900 mb-2">{{ displayItem.title }}</h4>
              <!-- 描述 -->
              <p class="text-sm text-slate-600 mb-4">{{ displayItem.description || '暂无描述' }}</p>
              <!-- 信息列表 -->
              <div class="space-y-3">
                <div class="flex items-center gap-3 text-sm">
                  <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                  <span class="text-slate-600">到期时间：</span>
                  <span :class="['text-slate-800', displayItem.overdue ? 'text-red-600' : '']">
                    {{ formatDate(displayItem.dueDate) }}
                  </span>
                </div>
                <div class="flex items-center gap-3 text-sm">
                  <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <span class="text-slate-600">创建时间：</span>
                  <span class="text-slate-800">{{ formatDateTime(displayItem.createTime) }}</span>
                </div>
                <div v-if="displayItem.completeTime" class="flex items-center gap-3 text-sm">
                  <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <span class="text-slate-600">完成时间：</span>
                  <span class="text-slate-800">{{ formatDateTime(displayItem.completeTime) }}</span>
                </div>
              </div>
            </template>
          </div>
          <!-- 底部按钮 -->
          <div v-if="displayItem && displayItem.status !== 'COMPLETED'" class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button
              @click="handleComplete"
              class="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              标记完成
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { todoApi } from '@/api/todo'
import { ElMessage } from 'element-plus'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  item: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close', 'complete'])

const loading = ref(false)

// 用于显示的详情数据，优先使用 fetchDetail 获取的完整数据
const displayItem = computed(() => detailItem.value || props.item)

// 优先级映射
const priorityMap = {
  1: { label: '低', class: 'px-2 py-0.5 bg-slate-100 text-slate-700 text-xs rounded-full' },
  2: { label: '中', class: 'px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full' },
  3: { label: '高', class: 'px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full' },
  4: { label: '紧急', class: 'px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full' }
}

// 状态映射
const statusMap = {
  'PENDING': { label: '待办', class: 'px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full' },
  'IN_PROGRESS': { label: '进行中', class: 'px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full' },
  'COMPLETED': { label: '已完成', class: 'px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full' },
  'OVERDUE': { label: '已逾期', class: 'px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full' },
  'cancelled': { label: '已取消', class: 'px-2 py-0.5 bg-slate-100 text-slate-700 text-xs rounded-full' }
}

// 获取优先级标签
function getPriorityLabel(priority) {
  return priorityMap[priority]?.label || '未知'
}

// 获取优先级徽章类
function getPriorityBadgeClass(priority) {
  return priorityMap[priority]?.class || priorityMap[2].class
}

// 获取状态标签
function getStatusLabel(status) {
  return statusMap[status]?.label || '未知'
}

// 获取状态徽章类
function getStatusBadgeClass(status) {
  return statusMap[status]?.class || statusMap['PENDING'].class
}

// 格式化日期
function formatDate(dateStr) {
  if (!dateStr) return '未设置'
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr.substring(0, 10)
  return datePart
}

// 格式化日期时间
function formatDateTime(dateStr) {
  if (!dateStr) return '-'
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr.substring(0, 10)
  const timePart = dateStr.includes('T') ? dateStr.split('T')[1]?.substring(0, 5) : ''
  return timePart ? `${datePart} ${timePart}` : datePart
}

// 关闭弹窗
function close() {
  emit('close')
}

// 完成待办
async function handleComplete() {
  if (!props.item) return

  try {
    await todoApi.complete(props.item.id)
    ElMessage.success('已完成')
    emit('complete')
    close()
  } catch (error) {
    console.error('完成待办失败:', error)
    ElMessage.error(error.message || '操作失败')
  }
}

// 详情数据
const detailItem = ref(null)

// 加载详情数据
async function fetchDetail() {
  if (!props.item?.id) return

  loading.value = true
  try {
    const res = await todoApi.getDetail(props.item.id)
    if (res) {
      detailItem.value = res
    }
  } catch (error) {
    console.error('获取待办详情失败:', error)
    ElMessage.error('加载详情失败')
  } finally {
    loading.value = false
  }
}

// 监听弹窗显示
watch(() => props.show, (val) => {
  if (val && props.item?.id) {
    fetchDetail()
  }
})
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: transform 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.95);
}
</style>
